import React, { useRef, useCallback } from 'react'
import { Portal } from 'react-portal'

import { Formik, FormikHelpers } from 'formik'
import * as yup from 'yup'
import {
  AccessLevelSelectInvite,
  MemberInviteForm,
  EmailField,
  InvitePeopleContent,
  InviteButton,
} from './InvitePeople.styles'
import { AccessLevelSelect } from '../AccessLevelSelect'
import { InvitePeopleField } from './InvitePeopleField'
import { ShareForSettingsModal } from '../types'
import { Separator } from '../SharingAndPermissions.styles'
import { ShareAddMemberWithPermissionsMutationVariables } from 'gqlTypes'
import PublicPermissions from '../PublicPermissions/PublicPermissions'
import { useAddMemberByEmail } from './useAddMemberByEmail'
import { useAddMemberByIdentifier } from './useAddMemberByIdentifier'
import ConfirmationDialog from 'components/ConfirmationDialog'

type Values = Pick<
  ShareAddMemberWithPermissionsMutationVariables['permissions'],
  'commentsEnabled' | 'privateAccessLevel'
> & {
  shouldValidateEmail: boolean
  email: string
  userIdentifier: string | null
}

const INITIAL_VALUES: Values = {
  shouldValidateEmail: true,
  email: '',
  commentsEnabled: false,
  privateAccessLevel: 'VIEW',
  userIdentifier: null,
}

const VALIDATION_SCHEMA = yup.object().shape({
  shouldValidateEmail: yup.boolean(),
  email: yup.string().max(255).required().when('shouldValidateEmail', {
    is: true,
    then: yup.string().trim().lowercase().email(),
  }),
  userIdentifier: yup.string().nullable(),
})

interface InvitePeopleProps {
  share: Pick<
    ShareForSettingsModal,
    | 'identifier'
    | 'publicUrl'
    | 'publicAccessLevel'
    | 'publicCommentsEnabled'
    | 'userCanUpdateCommentsEnabled'
    | 'allowedPrivateAccessLevels'
    | 'libraryEnabled'
    | 'name'
  >
}

const InvitePeople = ({ share }: InvitePeopleProps) => {
  const invitationInputRef = useRef<HTMLInputElement>(null)

  const {
    addMemberByEmail,
    addMemberByEmailLoading,
    getAddMemberByEmailConfirmDialogProps,
    addMemberByEmailConfirmDialogOpen,
  } = useAddMemberByEmail()

  const {
    userIdentifier,
    addMemberByAutocomplete,
    addMemberByAutocompleteLoading,
    getAddMemberByAutocompleteConfirmDialogProps,
    addMemberByAutocompleteConfirmDialogOpen,
  } = useAddMemberByIdentifier()

  const loading = addMemberByEmailLoading || addMemberByAutocompleteLoading

  const getConfirmDialogProps = (userIdentifier: string | undefined) =>
    userIdentifier
      ? getAddMemberByAutocompleteConfirmDialogProps()
      : getAddMemberByEmailConfirmDialogProps()

  const confirmDialogOpen =
    addMemberByEmailConfirmDialogOpen ||
    addMemberByAutocompleteConfirmDialogOpen

  const handleSubmit = useCallback(
    (values: Values, actions: FormikHelpers<Values>) => {
      actions.resetForm()

      if (values.userIdentifier) {
        return addMemberByAutocomplete({
          shareIdentifier: share.identifier,
          userIdentifier: values.userIdentifier,
          permissions: {
            commentsEnabled: values.commentsEnabled,
            privateAccessLevel: values.privateAccessLevel,
          },
        })
      }

      addMemberByEmail({
        identifier: share.identifier,
        permissions: {
          commentsEnabled: values.commentsEnabled,
          privateAccessLevel: values.privateAccessLevel,
        },
        shareMember: {
          email: values.email,
        },
      })
    },
    [addMemberByEmail, addMemberByAutocomplete, share]
  )

  return (
    <>
      <PublicPermissions share={share} />

      <Separator mt={5} mb={5} />

      <InvitePeopleContent data-testid="invite-people-form">
        <Formik<Values>
          validationSchema={VALIDATION_SCHEMA}
          validateOnBlur={false}
          initialValues={INITIAL_VALUES}
          onSubmit={handleSubmit}>
          {({ isValid, submitForm, values, setFieldValue }) => (
            <MemberInviteForm>
              <EmailField name="email">
                <>
                  <InvitePeopleField
                    shareIdentifier={share.identifier}
                    invitationInputRef={invitationInputRef}
                  />
                  <AccessLevelSelectInvite>
                    <AccessLevelSelect
                      access={{
                        kind: 'private',
                        level: values.privateAccessLevel,
                        setLevel: level =>
                          setFieldValue('privateAccessLevel', level),
                        allowedLevels: share.allowedPrivateAccessLevels,
                      }}
                      canUpdateCommentsEnabled={
                        values.privateAccessLevel !== 'EDIT'
                      }
                      commentsEnabled={
                        values.commentsEnabled ||
                        values.privateAccessLevel === 'EDIT'
                      }
                      setCommentsEnabled={enabled =>
                        setFieldValue('commentsEnabled', enabled)
                      }
                      onEnterClicked={() => {
                        if (isValid) {
                          submitForm()
                        }
                      }}
                    />
                  </AccessLevelSelectInvite>
                </>
              </EmailField>
              <InviteButton type="submit" loading={loading} disabled={!isValid}>
                Add
              </InviteButton>
            </MemberInviteForm>
          )}
        </Formik>
      </InvitePeopleContent>
      {confirmDialogOpen && (
        <Portal>
          <ConfirmationDialog
            {...getConfirmDialogProps(userIdentifier)}
            title={'Add yourself to this document?'}>
            You will no longer be able to edit this document. If you need Edit
            permissions again later, ask a Workspace Admin.
          </ConfirmationDialog>
        </Portal>
      )}
    </>
  )
}

export default InvitePeople
